import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import classNames from 'classnames';
import { subHours } from 'date-fns';
import ClickableTableRow from '@/shared/components/ClickableTableRow';
import Button from '@/shared/components/flip-ui-kit/Button';
import BoostPoolLogo from '@/shared/components/logo/BoostPoolLogo';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { isBlockExplorerApp, isLiquidityProvisionApp } from '@/shared/featureFlags';
import {
  getAllBoostPoolsVolumeAndFeesQuery,
  getEarnedFeesAndVolumeForPools,
} from '@/shared/graphql/lp/boost';
import { useAllBoostPools } from '@/shared/hooks/useAllBoostPools';
import { useBoostPoolApy } from '@/shared/hooks/useBoostPoolApy';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useTableSort from '@/shared/hooks/useTableSort';
import useTracking from '@/shared/hooks/useTracking';
import { SharedEvents, type SharedTrackEvents } from '@/shared/types';
import { apyToText } from '@/shared/utils/boost';
import { chainflipAssetMap } from '@/shared/utils/env';
import { COMPARATORS } from '@/shared/utils/helpers';
import { formatUsdValue, pipsToPercentString } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';

type BoostPoolRowProp = {
  asset: ChainflipAsset;
  tier: number;
  apy?: number;
  totalPoolLiquidity: bigint;
  totalPoolLiquidityUsd: number;
  availablePoolLiquidity?: bigint;
  availablePoolLiquidityUsd?: number;
  totalVolume?: bigint;
  volumeUsd: number;
  volumeUsdPrev24h?: number;
  fees24h: bigint;
  fees24hUsd: number;
  accountIdSs58?: string;
};

const Row = ({
  asset,
  tier,
  apy,
  totalPoolLiquidity,
  totalPoolLiquidityUsd,
  availablePoolLiquidity,
  availablePoolLiquidityUsd,
  totalVolume,
  volumeUsd,
  volumeUsdPrev24h,
  fees24h,
  fees24hUsd,
  accountIdSs58,
}: BoostPoolRowProp) => {
  const router = useRouter();

  const track = useTracking<SharedTrackEvents>();

  const token = chainflipAssetMap[asset];

  const volumeChange = isNotNullish(volumeUsdPrev24h) ? volumeUsd - volumeUsdPrev24h : undefined;
  const volumeChangeRelative =
    volumeChange && volumeUsdPrev24h && volumeUsdPrev24h !== 0
      ? volumeChange / volumeUsdPrev24h
      : 0;

  const totalVolumeCellContent = isNotNullish(totalVolume) ? (
    <>
      <span className="text-cf-white">
        {TokenAmount.fromAsset(totalVolume, asset).toFormatted()} {chainflipAssetMap[asset].symbol}
      </span>
      <div className="text-cf-light-2">{formatUsdValue(volumeUsd, false)}</div>
    </>
  ) : null;

  const volume24hCellContent = isNotNullish(volumeChange) ? (
    <>
      <div>{formatUsdValue(volumeUsd)}</div>
      <div
        className={classNames(
          'text-cf-light-2',
          volumeChange < 0 && '!text-cf-red-2',
          volumeChange > 0 && '!text-cf-green-1',
        )}
      >
        {formatUsdValue(volumeChange, undefined, true)}
        <span> {`(${(volumeChangeRelative * 100).toFixed(2)}%)`}</span>
      </div>
    </>
  ) : null;

  return (
    <ClickableTableRow
      className="h-[64px] font-aeonikMedium text-cf-white"
      href={
        isBlockExplorerApp
          ? `/pools/${asset}/boost?fee=${tier}`
          : `/boost/pools/${asset}/${tier}?table=Boosted+Deposits`
      }
    >
      <td>
        <div className="flex items-center gap-x-2 ">
          <BoostPoolLogo />
          <div>
            <div>BTC {pipsToPercentString(tier)}</div>
            <div className="text-cf-light-2">Bitcoin (Boost)</div>
          </div>
        </div>
      </td>

      <td className="text-20">{apyToText(apy)}</td>

      <td>
        <div className="flex flex-col">
          <span>
            {TokenAmount.fromAsset(totalPoolLiquidity, asset).toFormatted()} {token.symbol}
          </span>
          <span className="text-cf-light-2">{formatUsdValue(totalPoolLiquidityUsd, false)}</span>
        </div>
      </td>

      {isNotNullish(availablePoolLiquidity) && isNotNullish(availablePoolLiquidityUsd) && (
        <td>
          <div className="flex flex-col">
            <span>
              {TokenAmount.fromAsset(availablePoolLiquidity, asset).toFormatted()} {token.symbol}
            </span>
            <span className="text-cf-light-2">
              {formatUsdValue(availablePoolLiquidityUsd, false)}
            </span>
          </div>
        </td>
      )}

      <td>{totalVolumeCellContent ?? volume24hCellContent}</td>

      <td>
        <div className="flex flex-col">
          <span>
            {TokenAmount.fromAsset(fees24h, asset).toFormatted()} {chainflipAssetMap[asset].symbol}
          </span>
          <span className="text-cf-light-2">{formatUsdValue(fees24hUsd)}</span>
        </div>
      </td>

      {isLiquidityProvisionApp && (
        <td>
          <div className="flex justify-end gap-x-2">
            <Button
              type="secondary-standard"
              onClick={(e) => {
                e.stopPropagation();
                router.push(`/boost/pools/${asset}/${tier}?table=Boosted+Deposits`);
              }}
            >
              See pool
            </Button>
            <Button
              type="primary-standard"
              onClick={(e) => {
                e.stopPropagation();
                if (accountIdSs58) {
                  track(SharedEvents.AddBoostLiquidity, {
                    props: {
                      path: window.location.pathname,
                      scAccount: accountIdSs58,
                      feeTierBps: tier,
                      position: 'my_boost_positions',
                    },
                  });
                }
                router.push(`/boost/pools/${asset}/${tier}?tab=me`);
              }}
            >
              Add Liquidity
            </Button>
          </div>
        </td>
      )}
    </ClickableTableRow>
  );
};

const DEFAULT_SORTING = {
  col: 1,
  direction: 'DESC' as 'ASC' | 'DESC',
} as const;
const SORT_FUNCTIONS: ((poolData: Omit<BoostPoolRowProp, 'asset' | 'tier'>) => number | bigint)[] =
  [
    () => 0, // pool name is not sortable
    (poolData) => poolData.apy ?? 0,
    (poolData) => poolData.totalPoolLiquidity,
    ...(isBlockExplorerApp
      ? [
          (poolData: Omit<BoostPoolRowProp, 'asset' | 'tier'>) =>
            poolData.availablePoolLiquidity ?? 0,
        ]
      : []),
    (poolData) => poolData.totalVolume ?? poolData.volumeUsd ?? 0,
    (poolData) => poolData.fees24h,
  ];

export default function BoostPoolsOverviewTable({
  accountIdSs58,
  className,
}: {
  accountIdSs58?: string;
  className?: string;
}) {
  const initialDate = useMemo(() => new Date(), []);
  const { apys: apyData, isLoading: apyDataLoading } = useBoostPoolApy();

  const { data: aggData, isLoading: aggDataLoading } = useGqlQuery(
    getAllBoostPoolsVolumeAndFeesQuery,
    {
      variables: {
        date24HoursAgo: subHours(initialDate, 24).toISOString(),
        date48HoursAgo: subHours(initialDate, 48).toISOString(),
      },
      context: { clientName: 'lpProcessor' },
    },
  );

  const { data: volumeData, isLoading: volumeDataLoading } = useGqlQuery(
    getEarnedFeesAndVolumeForPools,
    {
      context: { clientName: 'lpProcessor' },
      refetchInterval: 30_000,
    },
  );

  const { boostPools: cachedBoostPools, isLoading: liquidityDataLoading } = useAllBoostPools();

  const { sort, setCol } = useTableSort({
    initialSort: DEFAULT_SORTING,
  });

  const poolTableRows = useMemo(
    () =>
      aggData?.pools?.nodes
        .map((item) => {
          const poolAggData = aggData.currentDay?.groupedAggregates?.find(
            (agg) => Number(agg.keys?.[0]) === item.id,
          )?.sum;
          const poolPrevDayAggData = aggData.previousDay?.groupedAggregates?.find(
            (agg) => Number(agg.keys?.[0]) === item.id,
          )?.sum;
          const cacheData = cachedBoostPools?.find(
            (node) => node.asset === item.asset && node.feeTierPips === item.feeTierPips,
          );
          const apy = apyData?.find(
            (d) => d.asset === item.asset && d.feeTierPips === item.feeTierPips,
          )?.apy;

          const totalVolume = volumeData?.pools?.nodes.find(
            (info) => info.asset === item.asset && info.feeTierPips === item.feeTierPips,
          )?.shares.aggregates?.sum;

          return {
            key: `${item.asset}-${item.feeTierPips}`,
            asset: item.asset,
            tier: item.feeTierPips,
            totalPoolLiquidity:
              BigInt(cacheData?.availableAmount ?? 0n) + BigInt(cacheData?.unavailableAmount ?? 0n),
            totalPoolLiquidityUsd:
              Number(cacheData?.availableAmountValueUsd ?? 0) +
              Number(cacheData?.unavailableAmountValueUsd ?? 0),
            availablePoolLiquidity: isBlockExplorerApp
              ? BigInt(cacheData?.availableAmount ?? 0)
              : undefined,
            availablePoolLiquidityUsd: isBlockExplorerApp
              ? Number(cacheData?.availableAmountValueUsd ?? 0)
              : undefined,
            totalVolume: isBlockExplorerApp ? undefined : BigInt(totalVolume?.amount ?? 0n),
            volumeUsd: isBlockExplorerApp
              ? Number(poolAggData?.amountUsd ?? 0)
              : Number(totalVolume?.amountUsd ?? 0),
            volumeUsdPrev24h: isBlockExplorerApp
              ? Number(poolPrevDayAggData?.amountUsd ?? 0)
              : undefined,
            fees24h: BigInt(poolAggData?.fee ?? 0n),
            fees24hUsd: Number(poolAggData?.feeUsd ?? 0),
            apy,
          };
        })
        .sort((a, b) =>
          COMPARATORS[sort.direction](SORT_FUNCTIONS[sort.col](a), SORT_FUNCTIONS[sort.col](b)),
        )
        .map((row) => (
          <Row
            key={row.key}
            asset={row.asset as ChainflipAsset}
            tier={row.tier}
            apy={row.apy}
            totalPoolLiquidity={row.totalPoolLiquidity}
            totalPoolLiquidityUsd={row.totalPoolLiquidityUsd}
            availablePoolLiquidity={row.availablePoolLiquidity}
            availablePoolLiquidityUsd={row.availablePoolLiquidityUsd}
            totalVolume={row.totalVolume}
            volumeUsd={row.volumeUsd}
            volumeUsdPrev24h={row.volumeUsdPrev24h}
            fees24h={row.fees24h}
            fees24hUsd={row.fees24hUsd}
            accountIdSs58={accountIdSs58}
          />
        )),
    [cachedBoostPools, aggData, apyData, volumeData, sort],
  );

  return (
    <TableV2
      columns={[
        { name: 'Pool', sortable: false },
        {
          name: 'APY',
          tooltip:
            'Annual Percentage Yield (APY) estimates the annual returns in a boost pool and fee tier. We take hourly snapshots of earned fees and boost liquidity, averaged over 72h, and project it over a year.',
        },
        isBlockExplorerApp ? 'Total Liquidity' : 'Liquidity',
        ...(isBlockExplorerApp ? ['Available Liquidity'] : []),
        isBlockExplorerApp ? 'Volume (24H)' : 'Volume',
        'Earned Fees (24H)',
        ...(isLiquidityProvisionApp ? [{ name: '', sortable: false }] : []),
      ]}
      rows={poolTableRows ?? []}
      isLoading={aggDataLoading || apyDataLoading || volumeDataLoading || liquidityDataLoading}
      thClassName="px-1 py-4 [&:first-child]:pl-4"
      tbodyClassName="[&>tr>td:first-child]:pl-4 [&>tr>td:last-child]:pr-4 [&>tr>td]:px-1 [&>tr>td]:py-4 [&>tr>td]:border-cf-gray-3-5"
      sorting={{ setCol, sort }}
      className={className}
    />
  );
}
